import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, 
  AlertCircle, 
  User, 
  CreditCard, 
  Calendar, 
  BadgeCheck, 
  GraduationCap, 
  Bus as BusIcon,
  MapPin,
  ArrowRight,
  Wallet
} from 'lucide-react';
import { Card, Button, Input, Select, Badge } from '../../components/ui/index.jsx';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';


const QuickPayment = () => {
    const { qrcode } = useParams();
    const navigate = useNavigate();
    
    const [student, setStudent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const { hasPermission, selectedYear } = useAuth();
    const canCreate = hasPermission('payments', 'create');
    const { success: toastSuccess, error: toastError } = useToast();

    useEffect(() => {
        if (!canCreate) {
            navigate('/dashboard');
        }
    }, [canCreate, navigate]);


    // Form states
    const [years, setYears] = useState([]);
    const [modes, setModes] = useState([]);
    const [monthlyPrice, setMonthlyPrice] = useState('');
    const [formData, setFormData] = useState({
        academic_year_id: selectedYear?.id || '',
        payment_mode_id: '',
        amount: '0',
        months: [],
        payment_date: new Date().toISOString().split('T')[0]
    });

    const monthsList = [
        { id: '09', name: 'سبتمبر' }, { id: '10', name: 'أكتوبر' }, { id: '11', name: 'نوفمبر' }, { id: '12', name: 'ديسمبر' },
        { id: '01', name: 'يناير' }, { id: '02', name: 'فبراير' }, { id: '03', name: 'مارس' }, { id: '04', name: 'أبريل' },
        { id: '05', name: 'مايو' }, { id: '06', name: 'يونيو' }, { id: '07', name: 'يوليو' }, { id: '08', name: 'أغسطس' }
    ];

    const handleMonthlyPriceChange = (val) => {
        setMonthlyPrice(val);
        const mPrice = parseFloat(val || 0);
        const rPrice = parseFloat(student?.registration_fee || 0);
        const total = ((formData.months?.length || 0) * mPrice) + (formData.is_registration_fee ? rPrice : 0);
        setFormData(prev => ({ ...prev, amount: total.toString() }));
    };

    const toggleRegistrationFee = () => {
        setFormData(prev => {
            const newValue = !prev.is_registration_fee;
            const monthCount = prev.months?.length || 0;
            const mPrice = parseFloat(monthlyPrice || student?.subscription_price || 0);
            const regPrice = parseFloat(student?.registration_fee || 0);
            const newAmount = (monthCount * mPrice) + (newValue ? regPrice : 0);
            return {
                ...prev,
                is_registration_fee: newValue,
                amount: newAmount.toString()
            };
        });
    };

    const toggleMonth = (monthName) => {
        const currentMonths = formData.months || [];
        let newMonths;
        if (currentMonths.includes(monthName)) {
            newMonths = currentMonths.filter(m => m !== monthName);
        } else {
            newMonths = [...currentMonths, monthName];
        }
        
        const mPrice = parseFloat(monthlyPrice || student?.subscription_price || 0);
        const regPrice = parseFloat(student?.registration_fee || 0);
        const newAmount = (newMonths.length * mPrice) + (formData.is_registration_fee ? regPrice : 0);

        setFormData(prev => ({
            ...prev,
            months: newMonths,
            amount: newAmount.toString()
        }));
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                // Fetch student by QR
                const studentRes = await api.get(`/students/qr/${qrcode}`);
                const studentData = studentRes.data.data;
                setStudent(studentData);
                
                const initialMonthly = studentData.subscription_price !== undefined ? String(studentData.subscription_price) : '0';
                setMonthlyPrice(initialMonthly);

                // Pre-fill amount
                setFormData(prev => ({
                    ...prev,
                    amount: '0',
                    months: [],
                    is_registration_fee: false,
                    academic_year_id: selectedYear?.id || studentData.academic_year_id || ''
                }));

                // Fetch years and modes
                const [yearsRes, modesRes] = await Promise.all([
                    api.get('/academic-years'),
                    api.get('/payment-modes')
                ]);
                const fetchedModes = modesRes.data.data || [];
                setYears(yearsRes.data.data || []);
                setModes(fetchedModes);

                // Auto-select ESPECE payment mode
                const especeMode = fetchedModes.find(m => m.name?.toUpperCase().includes('ESPECE') || m.name?.toUpperCase().includes('ESPÈCE'));
                if (especeMode) {
                    setFormData(prev => ({ ...prev, payment_mode_id: especeMode.id }));
                }

            } catch (err) {
                console.error(err);
                setError(err.response?.status === 404 ? 'التلميذ غير موجود' : 'حدث خطأ أثناء جلب البيانات');
            } finally {
                setLoading(false);
            }
        };

        if (qrcode) fetchInitialData();
    }, [qrcode]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate date against selected year
        if (selectedYear && formData.payment_date) {
            const date = formData.payment_date;
            if (date < selectedYear.start_date || date > selectedYear.end_date) {
                toastError(`تاريخ الدفع يجب أن يكون بين ${selectedYear.start_date} و ${selectedYear.end_date}`);
                return;
            }
        }

        setSubmitting(true);
        try {
            await api.post('/payments', {
                ...formData,
                student_id: student.id
            });
            toastSuccess('تم تسجيل الدفعة بنجاح');
            setSuccess(true);
            
        } catch (err) {
            toastError('خطأ أثناء تسجيل الدفعة');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
                <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
                <p className="text-slate-400 text-sm ">جاري التحقق من الرمز والبحث عن التلميذ...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-md mx-auto mt-12">
                <Card className="p-8 text-center border-red-100 bg-red-50/30">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h3 className="text-lg text-slate-800 mb-2">{error}</h3>
                    <p className="text-sm text-slate-500 mb-6 font-normal">تأكد من أن الرمز الممسوح صحيح وصالح في النظام.</p>
                    <Button variant="outline" onClick={() => navigate('/dashboard')}>
                        العودة للوحة التحكم
                    </Button>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="max-w-md mx-auto mt-12 animate-in zoom-in duration-300">
                <Card className="p-8 text-center border-emerald-100 bg-emerald-50/30 overflow-hidden relative">
                    <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500/20" />
                    
                    <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-200 rotate-3">
                        <CheckCircle2 className="w-10 h-10 text-white" />
                    </div>
                    
                    <h3 className="text-xl text-slate-800 mb-2 font-normal">تم بنجاح!</h3>
                    <div className="text-sm text-slate-500 mb-8 font-normal leading-relaxed text-center px-4">
                        تم تسجيل مبلغ <span className="text-emerald-600 font-normal">{formData.amount} د.م</span> للتلميذ <br/>
                        <span className="text-slate-800">{student?.full_name}</span>
                        {formData.months?.length > 0 && (
                            <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                                {formData.months.map(m => (
                                    <Badge key={m} variant="success" className="text-[10px] py-0.5 px-2 font-normal">{m}</Badge>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="space-y-3">
                        <Button 
                            className="w-full bg-slate-800 hover:bg-slate-900 text-white h-12 rounded-xl flex items-center justify-center gap-2"
                            onClick={() => window.location.href = '/dashboard/scanner'}
                        >
                            <User size={18} />
                            تسجيل دفعة جديدة (تلميذ آخر)
                        </Button>
                        
                        <p className="text-[11px] text-slate-400 font-normal">يمكنك الآن مسح رمز الـ QR للتلميذ التالي مباشرة</p>

                        <div className="pt-4 mt-4 border-t border-slate-100 flex items-center justify-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard/payments')}
                                className="text-xs text-primary hover:underline font-normal"
                            >
                                عرض وصل الدفع
                            </button>
                            <span className="w-1 h-1 rounded-full bg-slate-200" />
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="text-xs text-slate-400 hover:text-slate-600 font-normal"
                            >
                                العودة للرئيسية
                            </button>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center justify-between px-2">
                <div>
                    <h2 className="text-2xl text-slate-800 font-normal">تسجيل دفعة سريعة</h2>
                    <p className="text-slate-400 text-xs mt-1 font-normal">معالجة فورية للمدفوعات عبر رمز QR</p>
                </div>
                <Badge variant={student.is_active ? 'success' : 'danger'} className="px-3 py-1">
                    {student.is_active ? 'تلميذ نشط' : 'تلميذ غير نشط'}
                </Badge>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Student Info Card */}
                <Card className="lg:col-span-1 p-6 h-fit bg-white border-slate-100 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-primary/20" />
                    
                    <div className="flex flex-col items-center text-center space-y-4">
                        <div className="w-20 h-20 rounded-2xl bg-orange-50 text-primary flex items-center justify-center border border-orange-100 shadow-inner">
                            <User size={40} strokeWidth={1.5} />
                        </div>
                        <div>
                            <h3 className="text-lg text-slate-800 font-normal mb-1">{student.full_name}</h3>
                            <p className="text-[10px] text-slate-400 uppercase tracking-widest">{student.code_massar}</p>
                        </div>
                    </div>

                    <div className="mt-8 space-y-4">
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50/50 border border-slate-50 transition-colors hover:bg-slate-50">
                            <GraduationCap className="w-4 h-4 text-slate-400" />
                            <div className="flex flex-col">
                                <span className="text-[10px] text-slate-400 leading-none mb-1">المدرسة والقسم</span>
                                <span className="text-xs text-slate-700 font-normal">{student.school} - {student.school_class?.name || '--'}</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50/50 border border-slate-50 transition-colors hover:bg-slate-50">
                            <BusIcon className="w-4 h-4 text-slate-400" />
                            <div className="flex flex-col">
                                <span className="text-[10px] text-slate-400 leading-none mb-1">الحافلة والمنطقة</span>
                                <span className="text-xs text-slate-700 font-normal">{student.bus?.matricule || '--'} - {student.region?.region_name || '--'}</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 border border-primary/10">
                            <Wallet className="w-4 h-4 text-primary" />
                            <div className="flex flex-col">
                                <span className="text-[10px] text-primary/60 leading-none mb-1">واجب الاشتراك الشهري</span>
                                <span className="text-sm text-primary font-normal">{student.subscription_price || 0} د.م</span>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Payment Form Card */}
                <Card className="lg:col-span-2 p-8 bg-white border-slate-100 shadow-sm relative">

                    <div className="flex items-center gap-2 mb-8 text-slate-700">
                        <BadgeCheck className="w-5 h-5 text-emerald-500" />
                        <span className="text-base font-normal">تفاصيل العملية المالية</span>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Registration Fee Option */}
                        {student && student.registration_fee > 0 && (
                            <div className="space-y-3">
                                <label className="text-[13px] text-slate-500 font-normal block">واجب الانخراط السنوي :</label>
                                <button
                                    type="button"
                                    disabled={student.has_paid_registration_fee}
                                    onClick={toggleRegistrationFee}
                                    className={`w-full p-4 rounded-2xl border transition-all relative overflow-hidden text-right ${
                                        student.has_paid_registration_fee
                                        ? 'bg-slate-50 border-slate-100 opacity-60'
                                        : formData.is_registration_fee
                                            ? 'bg-emerald-50 border-emerald-200 shadow-sm ring-1 ring-emerald-100'
                                            : 'bg-slate-50 border-slate-100 hover:bg-white hover:border-slate-200'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                                student.has_paid_registration_fee || formData.is_registration_fee ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
                                            }`}>
                                                <BadgeCheck className="w-6 h-6" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-slate-800">الانخراط السنوي للموسم</p>
                                                <p className="text-[10px] text-slate-400 font-normal">يُدفع مرة واحدة كل سنة دراسية</p>
                                            </div>
                                        </div>
                                        <div className="text-lg font-bold text-slate-800">
                                            {student.registration_fee} د.م
                                        </div>
                                    </div>
                                    
                                    {(student.has_paid_registration_fee || formData.is_registration_fee) && (
                                        <div className="absolute top-0 right-0 w-6 h-6 bg-emerald-500 flex items-center justify-center rounded-bl-2xl shadow-sm">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                        </div>
                                    )}
                                    
                                    {student.has_paid_registration_fee && (
                                        <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center">
                                            <Badge variant="success" className="shadow-sm py-1 px-3">تم تسوية الوضعية للموسم الحالي</Badge>
                                        </div>
                                    )}
                                </button>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-[13px] text-slate-500 font-normal block">اختر الأشهر المراد دفعها:</label>
                                {student?.paid_months?.length > 0 && (
                                    <span className="text-[10px] text-emerald-600 font-medium bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100 flex items-center gap-1 animate-in fade-in">
                                        <BadgeCheck size={10} />
                                        المستوفاة: {student.paid_months.join('، ')}
                                    </span>
                                )}
                            </div>
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                {monthsList.map(month => {
                                    const isSelected = formData.months?.includes(month.name);
                                    const isAlreadyPaid = student.paid_months?.includes(month.name);
                                    
                                    return (
                                        <button
                                            key={month.id}
                                            type="button"
                                            onClick={() => !isAlreadyPaid && toggleMonth(month.name)}
                                            disabled={isAlreadyPaid}
                                            className={`relative py-2.5 text-[11px] rounded-xl border transition-all duration-200 ${
                                                isAlreadyPaid
                                                ? 'bg-emerald-50/50 text-emerald-400 border-emerald-100 cursor-not-allowed opacity-80'
                                                : isSelected 
                                                    ? 'bg-primary text-white border-primary shadow-md shadow-primary/20 transform scale-105' 
                                                    : 'bg-slate-50 text-slate-500 border-slate-100 hover:border-slate-300 hover:bg-white'
                                            }`}
                                        >
                                            {month.name}
                                            {isAlreadyPaid && (
                                                <div className="absolute -top-1 -right-1 bg-emerald-500 text-white rounded-full p-0.5 border-2 border-white shadow-sm">
                                                    <BadgeCheck size={8} />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Select 
                                label="السنة الدراسية" 
                                options={[
                                    { value: '', label: 'اختر السنة' }, 
                                    ...(years?.map(y => ({ value: y.id, label: y.name })) || [])
                                ]} 
                                value={formData.academic_year_id} 
                                onChange={e => setFormData({...formData, academic_year_id: e.target.value})} 
                                required
                                disabled
                                className="bg-slate-50/50 border-slate-200"
                            />
                            
                            <Input 
                                label="واجب الاشتراك الشهري (درهم)" 
                                type="number" 
                                value={monthlyPrice} 
                                onChange={e => handleMonthlyPriceChange(e.target.value)} 
                                placeholder="0"
                                required 
                                icon={Wallet}
                                className="bg-white border-slate-200 font-bold text-slate-800 text-lg"
                            />

                            <Input 
                                label="المبلغ الإجمالي المؤدى (درهم)" 
                                type="number" 
                                value={formData.amount} 
                                onChange={e => {
                                    const newTotal = e.target.value;
                                    setFormData(prev => ({ ...prev, amount: newTotal }));
                                    const mCount = formData.months?.length || 1;
                                    const rPrice = formData.is_registration_fee ? parseFloat(student?.registration_fee || 0) : 0;
                                    const subTotal = Math.max(0, parseFloat(newTotal || 0) - rPrice);
                                    setMonthlyPrice((subTotal / mCount).toFixed(2));
                                }} 
                                required 
                                icon={Wallet}
                                className="bg-slate-50 border-slate-200 font-bold text-primary text-lg"
                            />

                            <Select 
                                label="وسيلة الدفع" 
                                options={[
                                    { value: '', label: 'اختر وسيلة الدفع' }, 
                                    ...(modes?.map(m => ({ value: m.id, label: m.name })) || [])
                                ]} 
                                value={formData.payment_mode_id} 
                                onChange={e => setFormData({...formData, payment_mode_id: e.target.value})} 
                                required
                                className="bg-slate-50/50 border-slate-200"
                            />

                            <Input 
                                label="تاريخ الدفع" 
                                type="date" 
                                value={formData.payment_date} 
                                onChange={e => setFormData({...formData, payment_date: e.target.value})} 
                                required 
                                icon={Calendar}
                                className="bg-slate-50/50 border-slate-200"
                            />
                        </div>

                        {(formData.months?.length > 0 || formData.is_registration_fee) && (
                            <div className="p-4 rounded-xl bg-primary/5 border border-primary/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 animate-in fade-in zoom-in duration-300">
                                <div className="flex flex-col">
                                    <span className="text-[11px] text-primary uppercase font-bold tracking-wider">تفاصيل الحساب المالي</span>
                                    <p className="text-xs text-slate-700 mt-0.5">
                                        {formData.months?.length > 0 && (
                                            <span>
                                                {formData.months.length} أشهر ({formData.months.join('، ')}) × {monthlyPrice || 0} د.م = <span className="font-bold">{((formData.months.length) * parseFloat(monthlyPrice || 0)).toLocaleString()} د.م</span>
                                            </span>
                                        )}
                                        {formData.is_registration_fee && (
                                            <span className="text-emerald-700 font-semibold">
                                                {formData.months?.length > 0 ? ' + ' : ''}واجب انخراط سنوي {student?.registration_fee} د.م
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <span className="text-[10px] text-slate-400 block">المجموع الإجمالي</span>
                                    <span className="text-xl text-primary font-bold">{parseFloat(formData.amount || 0).toLocaleString()} د.م</span>
                                </div>
                            </div>
                        )}

                        <div className="pt-6 border-t border-slate-50">
                            <Button 
                                type="submit" 
                                isLoading={submitting} 
                                className="w-full h-12 text-base font-normal shadow-lg shadow-primary/20 rounded-xl"
                                disabled={!formData.months?.length}
                            >
                                تأكيد وتسجيل الدفعة الآن
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
            
            {/* Simple Footer/Actions */}
            <div className="flex justify-center">
                <button 
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center gap-2 text-xs text-slate-400 hover:text-primary transition-colors font-normal"
                >
                    إلغاء والعودة للرئيسية <ArrowRight size={14} className="rotate-180" />
                </button>
            </div>
        </div>
    );
};

export default QuickPayment;
